/**
 * Naming wood: the grammar behind every lumber label in the game — store
 * shelves, floor piles, machine bays, recipe text.
 *
 * The old name (`Pine Board (8'x4"x8/4, smooth, S4S)`) failed both
 * audiences at once: jargon-dense for a newcomer, wrong-shaped for a
 * woodworker (nobody lists dimensions length-first, and nobody calls a
 * 2x4 by its quarters). The two goals stop conflicting once the name
 * stops trying to carry everything:
 *
 * - The name says what a woodworker would say, keyed to what the wood
 *   *is*: nominal callouts for construction pine (`nominalSizeLabel`),
 *   quarters for everything else, both cross dimensions in feet for
 *   sheet goods (the tell that you're holding a sheet, not a board).
 *   Dimension order is cut-list order everywhere — thickness × width ×
 *   length — and lengths read as shop talk via `formatLength`
 *   (`4'` / `7"` / `3'6"`).
 * - The UI teaches what the name means, one layer down: the plain-inch
 *   tooltip (`describeStockDimensionsPlain`) and the manual's "Reading
 *   Lumber Sizes" article. Learning that 8/4 means two inches is a
 *   reward the game hands the player, not a prerequisite.
 *
 * Identity vs. state: a material's name (`getMaterialName`) carries
 * identity — species, notation, dimensions. What's been *done* to it —
 * surface, milling, end treatments — is state (`getMaterialState`),
 * rendered as a separate faded line under the name, never inside it.
 * Contexts that need one string use `getMaterialFullName` =
 * "name (state)", and grouping always keys on the full name: a smooth
 * board and a sanded board are different materials and must never
 * collapse into one row.
 *
 * Requirements keep their own voice (`describeMaterialRequirement`): a
 * machine bay or recipe input describes *constraints*, in cut-list
 * order, quarters always — a requirement for "any 2"-thick board" can't
 * be a 2x4, so nominal callouts never appear there.
 *
 * Out of scope, deliberately: real-world nominal shrinkage (a store 2x4
 * measuring 1.5" × 3.5"). The sim treats nominal as actual — milling
 * never consumes nominal dimension — so the labels follow the sim and
 * the callout stays honest.
 */
import { formatLength } from "../utils/formatNumber";
import { humanizeString } from "../utils/humanizeString";
import { initialPalletNails } from "./bench-work/pallet-geometry";
import { idMaker } from "../utils/idMaker";
import { InputMaterial, InputMaterialWithQuantity } from "./Machine";
import {
  Board,
  BOARD_DIMENSIONS,
  BoardDimension,
  defaultBoardFace,
  DustSpecies,
  EndGrainSlice,
  endsLabel,
  FinishedProduct,
  MaterialInstance,
  millingLabel,
  Pallet,
  Panel,
  panelSpecies,
  panelWidth,
  SheetGood,
  sheetDustSpecies,
  SheetGoodKind,
  Species,
  ToolItem,
  UnknownMaterial,
  FINISHED_PRODUCT_TYPES,
} from "./Materials";
import { TOOL_TYPES, ToolId } from "./Tool";

const makeId = idMaker();

export function makeMaterial<T extends MaterialInstance>(
  materialInitializer: Omit<T, "id">,
): T {
  const material = {
    ...materialInitializer,
    id: `m-${makeId()}`,
  } as T & MaterialInstance;
  // A board's face placement is pinned at mint time (see
  // defaultBoardFace) — operations that re-mint a board under a fresh
  // id must find it explicit, or they'd reroll its grain.
  if (material.type === "board" && material.face === undefined) {
    return {
      ...material,
      face: defaultBoardFace(material.id, material.length, material.width),
    } as T;
  }
  return material as T;
}

/** A tool as a physical object — what the store hands over and a rack gives back. */
export function makeToolItem(toolId: ToolId): ToolItem {
  return makeMaterial<ToolItem>({ type: "tool", toolId });
}

export function makePallet() {
  return makeMaterial<Pallet>({
    type: "pallet",
    deckBoards: [true, true, true, true, true, true, true, true],
    stringers: [true, true, true],
    nails: initialPalletNails(Array(8).fill(true), [true, true, true]),
  });
}

/**
 * The distinct species a material sheds when machined — what color its
 * sawdust is. Strip-built materials (panels, end-grain slices) report
 * every species they contain; sheet goods report their family's
 * pseudo-species (see docs/dust-and-cleaning.md).
 */
export function materialDustSpecies(
  material: MaterialInstance,
): ReadonlyArray<DustSpecies> {
  if ("strips" in material) {
    return [...new Set(material.strips.map((strip) => strip.species))];
  }
  if (material.type === "plywood") {
    return [sheetDustSpecies(material.kind)];
  }
  if ("species" in material) {
    const species: Species[] = [material.species];
    if (
      "accentSpecies" in material &&
      material.accentSpecies !== undefined &&
      material.accentSpecies !== material.species
    ) {
      species.push(material.accentSpecies);
    }
    return species;
  }
  return [];
}

export function isFinishedProduct(
  material: MaterialInstance,
): material is FinishedProduct {
  return (FINISHED_PRODUCT_TYPES as ReadonlyArray<string>).includes(
    material.type,
  );
}

/** Species display names for material names; humanizeString for the rest. */
const SPECIES_LABELS: Partial<Record<Species, string>> = {
  pallet: "Pallet Wood",
  purpleHeart: "Purple Heart",
};

export function speciesLabel(species: Species): string {
  return SPECIES_LABELS[species] ?? humanizeString(species);
}

/**
 * Store-sign names for the sheet-good kinds. The plywood grades read as
 * what they're for — cabinet, shop, sheathing — not as letter grades.
 */
const SHEET_KIND_LABELS: Record<SheetGoodKind, string> = {
  plywoodA: "Cabinet Plywood",
  plywoodB: "Shop Plywood",
  plywoodC: "Sheathing Plywood",
  mdf: "MDF",
  osb: "OSB",
  particleBoard: "Particle Board",
};

export function sheetKindLabel(kind: SheetGoodKind): string {
  return SHEET_KIND_LABELS[kind];
}

/**
 * The construction-lumber callout ("2x4") for boards on a true nominal
 * size, or null. Softwood only — hardwood always reads in quarters, no
 * matter its size — and only at a full 1" or 2" thickness. Crosscuts keep
 * the callout (a 4' 2x4 is still a 2x4); ripping or planing off the
 * nominal size dissolves it. See the module header.
 */
export function nominalSizeLabel(board: Board): string | null {
  if (board.species !== "pine") {
    return null;
  }
  if (board.thickness !== 4 && board.thickness !== 8) {
    return null;
  }
  if (board.width < 2) {
    return null;
  }
  return `${board.thickness / 4}x${board.width}`;
}

export function getMaterialName(material: MaterialInstance): string {
  switch (material.type) {
    case "board": {
      const { species, width, length, thickness } = material;
      const nominal = nominalSizeLabel(material);
      if (nominal) {
        return `${speciesLabel(species)} ${nominal} — ${formatLength(length)}`;
      }
      return `${speciesLabel(species)} ${thickness}/4 — ${width}" × ${formatLength(length)}`;
    }
    case "panel": {
      const species = panelSpecies(material);
      const speciesName =
        species.length === 1 ? speciesLabel(species[0]) : "Mixed Wood";
      const grainTag = material.grain === "end" ? "End-Grain " : "";
      return `${speciesName} ${grainTag}Panel ${material.thickness}/4 — ${panelWidth(
        material,
      )}" × ${formatLength(material.length)}`;
    }
    case "plywood": {
      // Sheet grammar: both cross dimensions read in feet (module header)
      const { kind, thickness, width, length } = material;
      return `${sheetKindLabel(kind)} ${thickness}/4 — ${formatLength(width)} × ${formatLength(length)}`;
    }
    case "endGrainSlice": {
      const species = [...new Set(material.strips.map((s) => s.species))];
      const speciesName =
        species.length === 1 ? humanizeString(species[0]) : "Mixed Wood";
      return `${speciesName} End-Grain Slice`;
    }
    case "tool":
      return TOOL_TYPES[material.toolId].name;
    default:
      if (isFinishedProduct(material)) {
        // "mineralOil" reads as "Oiled"; future finishes name themselves here
        const finishPrefix = material.finish === "mineralOil" ? "Oiled " : "";
        if (material.accentSpecies) {
          return `${finishPrefix}${humanizeString(
            material.species,
          )} & ${humanizeString(material.accentSpecies)} ${humanizeString(
            material.type,
          )}`;
        }
        return `${finishPrefix}${humanizeString(material.type)}`;
      }
      return humanizeString(material.type);
  }
}

/**
 * What's been done to a piece of stock — surface, milling, end
 * treatments — rendered as the faded line under its name, never inside
 * it (see the module header). Null for materials whose state never
 * varies.
 */
export function getMaterialState(material: MaterialInstance): string | null {
  switch (material.type) {
    case "board": {
      const milling = millingLabel(material);
      // "rough sawn" already says the surface is rough — don't stutter
      const stateTag =
        milling === "rough sawn" && material.surface === "rough"
          ? milling
          : milling
            ? `${material.surface}, ${milling}`
            : material.surface;
      // Mitered ends read like a cut list entry: "45° both ends"
      const ends = endsLabel(material);
      return ends ? `${stateTag}, ${ends}` : stateTag;
    }
    case "panel":
      return material.surface;
    default:
      return null;
  }
}

/**
 * Name and state in one string — the identity for grouping keys,
 * tooltips, and recipe output lists. Two boards may only share a list
 * row when their full names match: a smooth board and a sanded one are
 * different materials.
 */
export function getMaterialFullName(material: MaterialInstance): string {
  const name = getMaterialName(material);
  const state = getMaterialState(material);
  return state ? `${name} (${state})` : name;
}

/** "1-1/2" from 6 quarters — fractional inches as a woodworker writes them. */
function quartersToInches(quarters: number): string {
  const whole = Math.floor(quarters / 4);
  const rem = quarters % 4;
  const fraction = rem === 0 ? "" : rem === 2 ? "1/2" : `${rem}/4`;
  if (whole === 0) {
    return fraction;
  }
  return fraction ? `${whole}-${fraction}` : `${whole}`;
}

/**
 * A board or panel's size spelled out in plain inches — the teaching
 * tooltip that translates quarters notation ('2" thick · 4" wide · 8'
 * long'). Null for materials without the three axes.
 */
export function describeStockDimensionsPlain(
  material: MaterialInstance,
): string | null {
  if (material.type === "plywood") {
    return `${quartersToInches(material.thickness)}" thick · ${formatLength(material.width)} wide · ${formatLength(material.length)} long`;
  }
  if (material.type !== "board" && material.type !== "panel") {
    return null;
  }
  const width =
    material.type === "panel" ? panelWidth(material) : material.width;
  return `${quartersToInches(material.thickness)}" thick · ${width}" wide · ${formatLength(material.length)} long`;
}

export function materialToInput<T extends MaterialInstance = MaterialInstance>(
  material: T,
): InputMaterial<T> {
  const result: Record<string, unknown[]> = {};
  for (const key in material) {
    if (key !== "id") {
      result[key] = [material[key]];
    }
  }
  return result as InputMaterial<T>;
}

/**
 * The requirement fields this material fails ("matches" stands in for the
 * escape-hatch predicate). Empty means the material qualifies —
 * materialMeetsInput is exactly that check, so the two can never drift.
 * Refusal explanations key off these field names to say what's wrong.
 */
export function materialInputMismatches(
  material: MaterialInstance,
  inputMaterial: InputMaterial,
): string[] {
  const mismatches: string[] = [];
  if (inputMaterial.matches && !inputMaterial.matches(material)) {
    mismatches.push("matches");
  }
  if (
    inputMaterial.minPanelWidth !== undefined &&
    (material.type !== "panel" ||
      panelWidth(material) < inputMaterial.minPanelWidth)
  ) {
    mismatches.push("minPanelWidth");
  }
  for (const key of Object.keys(inputMaterial)) {
    // Skip the constraint keys that aren't properties of the material
    if (
      key === "quantity" ||
      key === "matches" ||
      key === "matchesNote" ||
      key === "minPanelWidth"
    ) {
      continue;
    } else if (
      !(key in material) ||
      !(inputMaterial as Record<string, unknown[]>)[key].includes(
        (material as Record<string, unknown>)[key],
      )
    ) {
      mismatches.push(key);
    }
  }
  return mismatches;
}

export function materialMeetsInput(
  material: MaterialInstance,
  inputMaterial: InputMaterial,
) {
  return materialInputMismatches(material, inputMaterial).length === 0;
}

// Helper to create a mock material from a requirement for placeholder display
export function createMockMaterial(
  requirement: InputMaterialWithQuantity,
): MaterialInstance {
  // No type listed means the recipe takes anything at all (the garbage
  // can's Empty). A bare board stands in for "some piece of stock" —
  // placeholders are only ever drawn in an empty slot, and a requirement
  // this loose has nothing more specific to say.
  const requiredType = requirement.type?.[0] ?? "board";

  switch (requiredType) {
    case "board": {
      const r = requirement as InputMaterialWithQuantity<Board>;
      return makeMaterial<Board>({
        type: "board",
        length: r.length?.[0] || 96,
        width: r.width?.[0] || 4,
        thickness: r.thickness?.[0] || 2,
        species: r.species?.[0] || "pine",
        surface: r.surface?.[0] || "rough",
        jointedFaces: r.jointedFaces?.[0] ?? 2,
        jointedEdges: r.jointedEdges?.[0] ?? 2,
      });
    }
    case "plywood": {
      const r = requirement as InputMaterialWithQuantity<SheetGood>;
      return makeMaterial<SheetGood>({
        type: "plywood",
        kind: r.kind?.[0] || "plywoodA",
        length: r.length?.[0] || 96,
        width: r.width?.[0] || 48,
        thickness: (r.thickness?.[0] || 2) as SheetGood["thickness"],
      });
    }

    case "pallet":
      return makeMaterial<Pallet>({
        type: "pallet",
        deckBoards: [true, true, true, true, true, true, true, true],
        stringers: [true, true, true],
        nails: initialPalletNails(Array(8).fill(true), [true, true, true]),
      });

    case "endGrainSlice":
      // Placeholder display only; a representative single-species slice
      return makeMaterial<EndGrainSlice>({
        type: "endGrainSlice",
        thickness: 4,
        strips: Array.from({ length: 5 }, () => ({
          species: "maple",
          width: 2,
        })),
      });

    case "jewelryBox":
    case "rusticShelf":
    case "shelf":
    case "planterBox":
    case "rusticFrame":
    case "pictureFrame":
    case "simpleCuttingBoard":
    case "stripedCuttingBoard":
    case "sunriseCuttingBoard":
    case "endGrainCuttingBoard":
    case "birdhouse":
    case "crate":
    case "stepStool":
    case "hexFrame":
    case "servingTray":
    case "bookshelf":
    case "sideTable":
    case "checkerboardCuttingBoard": {
      // Carry accent and finish through so requirement-priced work values
      // an oiled or two-tone ask correctly
      const r = requirement as InputMaterialWithQuantity<FinishedProduct>;
      return makeMaterial<FinishedProduct>({
        type: requiredType,
        species: r.species?.[0] ?? "pine",
        ...(r.accentSpecies?.[0] !== undefined
          ? { accentSpecies: r.accentSpecies[0] }
          : {}),
        ...(r.finish?.[0] !== undefined ? { finish: r.finish[0] } : {}),
      });
    }

    case "panel": {
      // Placeholder display only; a representative single-species blank.
      // Wide-panel requirements get enough strips to actually meet (and
      // price at) their minimum width.
      const r = requirement as InputMaterialWithQuantity<Panel>;
      const stripCount = Math.max(
        5,
        Math.ceil((requirement.minPanelWidth ?? 0) / 2),
      );
      return makeMaterial<Panel>({
        type: "panel",
        length: 24,
        thickness: r.thickness?.[0] || 4,
        surface: r.surface?.[0] || "rough",
        strips: Array.from({ length: stripCount }, () => ({
          species: "maple",
          width: 2,
        })),
      });
    }

    case "tool": {
      const r = requirement as InputMaterialWithQuantity<ToolItem>;
      return makeToolItem(r.toolId?.[0] ?? "hammer");
    }

    case "unknown":
      return makeMaterial<UnknownMaterial>({
        type: "unknown",
      });

    default:
      return assertUnreachable(requiredType);
  }
}

function assertUnreachable(x: never): never {
  throw new Error(`Unexpected object: ${x}`);
}

/** Join a list of choices with commas and a trailing "or" ("a", "a or b", "a, b, or c"). */
function joinOr(parts: ReadonlyArray<string>): string {
  if (parts.length <= 1) {
    return parts[0] ?? "";
  }
  if (parts.length === 2) {
    return `${parts[0]} or ${parts[1]}`;
  }
  return `${parts.slice(0, -1).join(", ")}, or ${parts[parts.length - 1]}`;
}

// Suffix that turns a raw dimension value into a readable measurement.
// Width is inches, thickness quarter-inches — matching getMaterialName.
// Lengths are stored in inches but read as shop talk via formatLength.
const DIMENSION_UNITS = {
  length: "",
  width: '"',
  thickness: "/4",
} as const;
type DimensionKey = keyof typeof DIMENSION_UNITS;
// Cut-list order: thickness × width × length (see the module header).
const DIMENSION_KEYS: ReadonlyArray<DimensionKey> = [
  "thickness",
  "width",
  "length",
];

/**
 * Attributes worth spelling out — including an explicit "any" when the
 * requirement leaves them unconstrained — for each stock type where
 * dimensions and surface actually matter, in reading order. Requirement
 * types absent from this map (finished products, pallets, …) only list the
 * constraints they actually carry.
 */
const DESCRIBABLE_ATTRIBUTES: Partial<
  Record<MaterialInstance["type"], ReadonlyArray<string>>
> = {
  board: [
    "species",
    "surface",
    "jointedFaces",
    "jointedEdges",
    "thickness",
    "width",
    "length",
  ],
  plywood: ["kind", "thickness", "width", "length"],
  panel: ["surface", "grain", "thickness", "length"],
};

// Constraint keys we know how to phrase, in the order they read best. Keeping
// this list in sync with what materialMeetsInput checks is what keeps the
// description honest — every key here is a key the matcher enforces.
const DESCRIBABLE_KEYS: ReadonlyArray<string> = [
  "species",
  "accentSpecies",
  "finish",
  "kind",
  "surface",
  "grain",
  "jointedFaces",
  "jointedEdges",
  "thickness",
  "width",
  "length",
];

/** The milling axes only speak up when a requirement actually constrains them. */
const JOINTED_KEYS = ["jointedFaces", "jointedEdges"] as const;
type JointedKey = (typeof JOINTED_KEYS)[number];

/**
 * Phrase a milling constraint (see JointedCount in Materials.ts) as shop
 * language instead of counts. Today's operations use thresholds ("at least
 * a reference face": [1, 2]), completions ([2]), or untouched stock ([0]).
 */
function describeJointedConstraint(
  key: JointedKey,
  values: ReadonlyArray<unknown>,
): string {
  const set = new Set(values as number[]);
  const faces = key === "jointedFaces";
  if (set.has(1) && set.has(2)) {
    return faces ? "a flat face" : "a straight edge";
  }
  if (set.has(2)) {
    return faces ? "faces planed parallel" : "edges ripped parallel";
  }
  if (set.has(1)) {
    return faces ? "one flat face" : "one straight edge";
  }
  return faces ? "faces not yet jointed" : "edges not yet jointed";
}

function requirementValues(
  req: InputMaterialWithQuantity,
  key: string,
): ReadonlyArray<unknown> | undefined {
  return (req as unknown as Record<string, ReadonlyArray<unknown> | undefined>)[
    key
  ];
}

/**
 * Plain-English summary of what a material requirement accepts, walking the
 * same keys materialMeetsInput enforces so display and matching stay in sync.
 * Renders dimensions ("2'×4"×1/4"), species, surface, etc.; unconstrained
 * attributes of dimensioned stock read as "any species" / "any surface".
 * Quantity is not included — callers render have/need counts separately.
 */
export function describeMaterialRequirement(
  req: InputMaterialWithQuantity,
): string {
  const types = req.type?.length
    ? joinOr(req.type.map((t) => humanizeString(t)))
    : "Material";

  const typeKey = req.type?.length === 1 ? req.type[0] : undefined;
  const applicable =
    (typeKey && DESCRIBABLE_ATTRIBUTES[typeKey]) ??
    // Types without a full attribute list only describe present constraints.
    DESCRIBABLE_KEYS.filter((key) => requirementValues(req, key) !== undefined);

  const qualifiers: string[] = [];
  // A matches predicate is opaque to this walker; its note speaks for it
  if (req.matchesNote) {
    qualifiers.push(req.matchesNote);
  }
  const presentDims: DimensionKey[] = [];

  for (const key of applicable) {
    if (DIMENSION_KEYS.includes(key as DimensionKey)) {
      if (requirementValues(req, key) !== undefined) {
        presentDims.push(key as DimensionKey);
      }
      continue;
    }
    const values = requirementValues(req, key);
    if (JOINTED_KEYS.includes(key as JointedKey)) {
      // Unconstrained milling state stays silent — "any jointed faces" is
      // noise on the many requirements that don't care about milling.
      if (values !== undefined) {
        qualifiers.push(describeJointedConstraint(key as JointedKey, values));
      }
    } else if (values === undefined) {
      qualifiers.push(`any ${humanizeString(key).toLowerCase()}`);
    } else if (key === "grain") {
      qualifiers.push(
        `${joinOr(values.map((v) => humanizeString(String(v)).toLowerCase()))} grain`,
      );
    } else if (key === "kind") {
      // Sheet kinds read by their store-sign names, never letter grades
      qualifiers.push(
        joinOr(values.map((v) => sheetKindLabel(v as SheetGoodKind))),
      );
    } else {
      qualifiers.push(joinOr(values.map((v) => humanizeString(String(v)))));
    }
  }

  // Dimensions: compact "L'×W"×T/4" when all three are present and each is a
  // single value; otherwise describe each present dimension on its own.
  const allDimsSingle =
    presentDims.length === DIMENSION_KEYS.length &&
    presentDims.every((key) => requirementValues(req, key)!.length === 1);

  // Lengths (and a sheet's foot-scale width) read as shop talk; the
  // cross-section detents keep their plain unit suffixes
  const formatDim = (key: DimensionKey, value: unknown) =>
    key === "length" || (key === "width" && typeKey === "plywood")
      ? formatLength(value as number)
      : `${value}${DIMENSION_UNITS[key]}`;

  const dimClauses: string[] = [];
  if (allDimsSingle) {
    dimClauses.push(
      DIMENSION_KEYS.map((key) =>
        formatDim(key, requirementValues(req, key)![0]),
      ).join("×"),
    );
  } else {
    for (const key of DIMENSION_KEYS) {
      const values = requirementValues(req, key);
      const label = humanizeString(key).toLowerCase();
      if (values === undefined) {
        if (applicable.includes(key)) {
          dimClauses.push(`any ${label}`);
        }
      } else {
        dimClauses.push(
          `${label} ${joinOr(values.map((v) => formatDim(key, v)))}`,
        );
      }
    }
  }

  if (req.minPanelWidth !== undefined) {
    dimClauses.push(`at least ${req.minPanelWidth}" wide`);
  }

  const details = [...qualifiers, ...dimClauses];
  return details.length ? `${types} (${details.join(", ")})` : types;
}
